import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  setBreadcrumbs,
  getApplication,
  getAllowedTokenExchangePermissions,
  getRegistrationsForApplication,
  createTokenExchangeRequest,
  deleteTokenExchangePermission,
  getGrantedTokenExchangePermissions,
} from "../../actions/index";
import {
  Row,
  Col,
  Table,
  Button,
  Modal,
  Typography,
  Tooltip,
  Layout,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import RegistrationSearchModal from "./RegistrationSearchModal";
import { errorAction, types as at } from "@authzsvc/api-lib";
import { showErrorNotification } from "../../utils";
import { FullPageLoader } from "@authzsvc/common-react-components";

const { Title, Paragraph } = Typography;
const { Content } = Layout;

const TokenExchangePanel = ({
  setBreadcrumbs,
  getAllowedTokenExchangePermissions,
  getApplication,
  getRegistrationsForApplication,
  createTokenExchangeRequest,
  deleteTokenExchangePermission,
  getGrantedTokenExchangePermissions,
  breadcrumbs,
  application,
  allowedTokenExchangePermissions,
  grantedTokenExchangePermissions,
  registrations,
}) => {
  const { regid, appid } = useParams();

  const [loadingAllowed, setLoadingAllowed] = useState(false);
  const [loadingGranted, setLoadingGranted] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [openAllowedPicker, setOpenAllowedPicker] = useState(false);
  const [openGrantedPicker, setOpenGrantedPicker] = useState(false);

  const [modalContext, setModalContext] = useState({
    type: null, // "allowed" or "granted"
    permission: null,
  });

  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
  }, [setBreadcrumbs, breadcrumbs]);

  useEffect(() => {
    const applicationNotLoaded =
      !application || Object.keys(application).length === 0;
    const loadApplication = async () => {
      setLoadingAllowed(true);
      setLoadingGranted(true);
      if (applicationNotLoaded) {
        await Promise.all([
          getApplication(appid),
          getRegistrationsForApplication(appid),
        ]);
      }
      await getAllowedTokenExchangePermissions(regid);
      await getGrantedTokenExchangePermissions(regid);
      setLoadingAllowed(false);
      setLoadingGranted(false);
    };
    loadApplication();
  }, [
    application,
    getApplication,
    getRegistrationsForApplication,
    getAllowedTokenExchangePermissions,
    getGrantedTokenExchangePermissions,
    regid,
    appid,
  ]);

  const addNewTokenExchange = async (targetRegistration, reason) => {
    setLoadingAllowed(true);
    setLoadingGranted(true);
    const tokenRequestObject = {
      registrationId: targetRegistration.id,
      allowedRegistrationId: regid,
      comment: reason,
    };

    const createResp = await createTokenExchangeRequest(tokenRequestObject);
    if (
      createResp.type ===
      errorAction(at.PUT_TOKEN_EXCHANGE_REQUEST_WITH_BODY_PARAMS)
    ) {
      showErrorNotification(
        createResp.payload.title,
        createResp.payload.data.message,
      );
    }
    await getAllowedTokenExchangePermissions(regid);
    await getGrantedTokenExchangePermissions(regid);
    setLoadingAllowed(false);
    setLoadingGranted(false);
  };

  const grantNewTokenExchange = async (targetRegistration) => {
    setLoadingAllowed(true);
    setLoadingGranted(true);
    const tokenRequestObject = {
      registrationId: regid,
      allowedRegistrationId: targetRegistration.id,
    };
    const createResp = await createTokenExchangeRequest(tokenRequestObject);
    if (
      createResp.type ===
      errorAction(at.PUT_TOKEN_EXCHANGE_REQUEST_WITH_BODY_PARAMS)
    ) {
      showErrorNotification(
        createResp.payload.title,
        createResp.payload.data.message,
      );
    }
    await getAllowedTokenExchangePermissions(regid);
    await getGrantedTokenExchangePermissions(regid);
    setLoadingAllowed(false);
    setLoadingGranted(false);
  };

  const getCurrentRegistration = () => {
    const reg = registrations.find((r) => r.registrationId === regid);
    if (reg) {
      return reg.registration.authenticationProviderId;
    }
  };

  const deleteGrantedPermission = async (target) => {
    setLoadingGranted(true);
    const resp = await deleteTokenExchangePermission(
      regid,
      target.registration.id,
    );
    if (resp.type === errorAction(at.DELETE_TOKEN_EXCHANGE_PERMISSION)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
    }
    await getGrantedTokenExchangePermissions(regid);
    setLoadingGranted(false);
    setOpenDeleteModal(false);
    setModalContext({ type: null, permission: null });
  };

  const deleteAllowedPermission = async (target) => {
    setLoadingAllowed(true);
    const resp = await deleteTokenExchangePermission(
      target.registration.id,
      regid,
    );
    if (resp.type === errorAction(at.DELETE_TOKEN_EXCHANGE_PERMISSION)) {
      showErrorNotification(resp.payload.title, resp.payload.data.message);
    }
    await getAllowedTokenExchangePermissions(regid);
    setLoadingAllowed(false);
    setOpenDeleteModal(false);
    setModalContext({ type: null, permission: null });
  };

  const authenticationProviderId = getCurrentRegistration();
  if (!authenticationProviderId) {
    return <FullPageLoader />;
  }

  return (
    <Content
      style={{
        padding: 24,
      }}
    >
      <Row gutter={[16, 16]}>
        {/* Allowed */}
        <Col span={12}>
          <div
            style={{
              background: "#fff",
              padding: 24,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              borderRadius: 8,
            }}
          >
            <Title level={3}>Allowed Permissions</Title>
            <Paragraph>
              Your application is able to exchange tokens for the following
              applications.
            </Paragraph>

            {loadingAllowed ? (
              <FullPageLoader />
            ) : (
              <Table
                dataSource={allowedTokenExchangePermissions || []}
                rowKey="registrationName"
                pagination={false}
                locale={{
                  emptyText:
                    "You are not able to exchange tokens for any applications yet",
                }}
                columns={[
                  {
                    title: "Registration Name",
                    dataIndex: "registrationName",
                    searchable: true,
                  },
                  {
                    title: "Description",
                    dataIndex: ["registration", "description"],
                    searchable: true,
                    render: (text, te) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                      >
                        <span>{text}</span>
                        <Tooltip title="Revoke permission">
                          <Button
                            id="revokeAllowedButton"
                            color="red"
                            variant="filled"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              setModalContext({
                                type: "allowed",
                                permission: te,
                              });
                              setOpenDeleteModal(true);
                            }}
                          />
                        </Tooltip>
                      </div>
                    ),
                  },
                ]}
                footer={() =>
                  authenticationProviderId && (
                    <>
                      <Button
                        block
                        id="requestAllowedPermissionButton"
                        color="blue"
                        variant="filled"
                        icon={<PlusOutlined />}
                        onClick={() => setOpenAllowedPicker(true)}
                      >
                        Request token exchange permissions
                      </Button>

                      <RegistrationSearchModal
                        title="Request token exchange permissions"
                        allowsComment
                        onSelect={addNewTokenExchange}
                        authenticationProviderId={authenticationProviderId}
                        open={openAllowedPicker}
                        onOpenChange={setOpenAllowedPicker}
                      />
                    </>
                  )
                }
              />
            )}
          </div>
        </Col>

        {/* Granted */}
        <Col span={12}>
          <div
            style={{
              background: "#fff",
              padding: 24,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              borderRadius: 8,
            }}
          >
            <Title level={3}>Granted Permissions</Title>
            <Paragraph>
              The following applications can exchange tokens for your
              application.
            </Paragraph>

            {loadingGranted ? (
              <FullPageLoader />
            ) : (
              <Table
                dataSource={grantedTokenExchangePermissions || []}
                rowKey="registrationName"
                pagination={false}
                locale={{
                  emptyText:
                    "You have not granted any exchange permissions yet",
                }}
                columns={[
                  {
                    title: "Registration Name",
                    dataIndex: "registrationName",
                    searchable: true,
                  },
                  {
                    title: "Description",
                    dataIndex: ["registration", "description"],
                    searchable: true,
                    render: (text, te) => (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                      >
                        <span>{text}</span>
                        <Tooltip title="Revoke permission">
                          <Button
                            id="revokeGrantedButton"
                            color="red"
                            variant="filled"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              setModalContext({
                                type: "granted",
                                permission: te,
                              });
                              setOpenDeleteModal(true);
                            }}
                          />
                        </Tooltip>
                      </div>
                    ),
                  },
                ]}
                footer={() =>
                  authenticationProviderId && (
                    <>
                      <Button
                        block
                        id="grantPermissionButton"
                        color="blue"
                        variant="filled"
                        icon={<PlusOutlined />}
                        onClick={() => setOpenGrantedPicker(true)}
                      >
                        Grant token exchange permissions
                      </Button>

                      <RegistrationSearchModal
                        title="Grant token exchange permissions"
                        allowsComment={false}
                        onSelect={grantNewTokenExchange}
                        authenticationProviderId={authenticationProviderId}
                        open={openGrantedPicker}
                        onOpenChange={setOpenGrantedPicker}
                      />
                    </>
                  )
                }
              />
            )}
          </div>
        </Col>
      </Row>

      {/* Revoke Confirmation Modal */}
      <Modal
        title={
          modalContext.type === "allowed"
            ? "Revoke Allowed Permission"
            : "Revoke Granted Permission"
        }
        open={openDeleteModal}
        onCancel={() => {
          setOpenDeleteModal(false);
          setModalContext({ type: null, permission: null });
        }}
        onOk={async () => {
          if (modalContext.type === "allowed") {
            await deleteAllowedPermission(modalContext.permission);
          } else if (modalContext.type === "granted") {
            await deleteGrantedPermission(modalContext.permission);
          }
          setOpenDeleteModal(false);
          setModalContext({ type: null, permission: null });
        }}
        okText="Delete Permission"
        okButtonProps={{ danger: true, type: "primary" }}
        centered
        closable
        width={520}
      >
        <p>
          Are you sure you want to revoke{" "}
          {modalContext.type === "granted"
            ? "the permission granted to"
            : "permission to"}
          :<strong> {modalContext.permission?.registrationName}</strong>?
        </p>
      </Modal>
    </Content>
  );
};

TokenExchangePanel.propTypes = {
  application: PropTypes.object,
  registrations: PropTypes.array,
  allowedTokenExchangePermissions: PropTypes.array,
  grantedTokenExchangePermissions: PropTypes.array,
  setBreadcrumbs: PropTypes.func,
  getApplication: PropTypes.func,
  getAllowedTokenExchangePermissions: PropTypes.func,
  getRegistrationsForApplication: PropTypes.func,
  createTokenExchangeRequest: PropTypes.func,
  deleteTokenExchangePermission: PropTypes.func,
  getGrantedTokenExchangePermissions: PropTypes.func,
  match: PropTypes.shape({
    params: PropTypes.shape({
      regid: PropTypes.string,
      appid: PropTypes.string,
    }),
  }),
  breadcrumbs: PropTypes.arrayOf(PropTypes.object),
};

export default connect(
  (state) => ({
    application: state.active_application.application,
    registrations: state.active_application.registrations,
    allowedTokenExchangePermissions: state.tokenExchange.allowed,
    grantedTokenExchangePermissions: state.tokenExchange.granted,
  }),
  {
    setBreadcrumbs,
    getApplication,
    getAllowedTokenExchangePermissions,
    getRegistrationsForApplication,
    getGrantedTokenExchangePermissions,
    createTokenExchangeRequest,
    deleteTokenExchangePermission,
  },
)(TokenExchangePanel);
